'use strict';

const STORE_KEY = 'rmt-items';
const VALID_PRIORITIES = new Set(['low', 'medium', 'high']);
const VALID_VISIBILITIES = new Set(['private', 'public']);

const defaultItems = [
  {
    id: 'item-1',
    title: 'Review Q3 metrics',
    description: 'Prepare and review the Q3 metrics deck.',
    priority: 'high',
    status: 'todo',
    dueDate: new Date().toISOString().slice(0, 10),
    visibility: 'public',
    createdBy: 'demo-user',
    assignees: ['demo-user'],
    createdAt: new Date().toISOString(),
  },
  {
    id: 'item-2',
    title: 'Draft project brief',
    description: 'Write the first draft of the project brief.',
    priority: 'medium',
    status: 'todo',
    dueDate: '',
    visibility: 'private',
    createdBy: 'demo-user',
    assignees: ['demo-user'],
    createdAt: new Date().toISOString(),
  },
  {
    id: 'item-3',
    title: 'Sync with design',
    description: 'Align with design on wireframes.',
    priority: 'low',
    status: 'in_progress',
    dueDate: '',
    visibility: 'private',
    createdBy: 'demo-user',
    assignees: ['demo-user'],
    createdAt: new Date().toISOString(),
  },
];

function load() {
  const raw = globalThis.localStorage?.getItem(STORE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function save(items) {
  globalThis.localStorage?.setItem(STORE_KEY, JSON.stringify(items));
}

/**
 * Return the current item list, seeding with demo data on first use.
 */
export function getItems() {
  let items = load();
  if (!items) {
    items = structuredClone ? structuredClone(defaultItems) : JSON.parse(JSON.stringify(defaultItems));
    save(items);
  }
  return items;
}

/**
 * Reset the store to the demo seed data. Useful for tests and demos.
 */
export function resetItems() {
  const items = structuredClone ? structuredClone(defaultItems) : JSON.parse(JSON.stringify(defaultItems));
  save(items);
  return items;
}

/**
 * Create a new item. The current demo user is hardcoded as the owner
 * and assignee until the auth slice is implemented.
 */
export function createItem(fields) {
  const items = getItems();
  const item = {
    id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: fields.title,
    description: fields.description || '',
    priority: VALID_PRIORITIES.has(fields.priority) ? fields.priority : 'medium',
    status: fields.status || 'todo',
    dueDate: fields.dueDate || '',
    visibility: VALID_VISIBILITIES.has(fields.visibility) ? fields.visibility : 'private',
    createdBy: fields.createdBy || 'demo-user',
    assignees: fields.assignees?.length ? fields.assignees : ['demo-user'],
    createdAt: new Date().toISOString(),
  };
  items.push(item);
  save(items);
  return item;
}

/**
 * Update a subset of fields on an existing item.
 */
export function updateItem(id, patch) {
  const items = getItems();
  const index = items.findIndex((i) => i.id === id);
  if (index === -1) return null;
  items[index] = { ...items[index], ...patch };
  save(items);
  return items[index];
}

/**
 * Delete an item by id.
 */
export function deleteItem(id) {
  const items = getItems();
  const index = items.findIndex((i) => i.id === id);
  if (index === -1) return null;
  const removed = items.splice(index, 1)[0];
  save(items);
  return removed;
}

/**
 * List items visible to the given role. Viewers only see public items.
 */
export function listForRole(role) {
  const items = getItems();
  if (role === 'admin' || role === 'user') return items;
  if (role === 'viewer') return items.filter((i) => i.visibility === 'public');
  return [];
}

/**
 * Return a user's personal inbox (items they created or are assigned to).
 */
export function inboxForUser(userId) {
  const items = getItems();
  return items.filter(
    (i) => i.createdBy === userId || i.assignees.includes(userId)
  );
}

/**
 * Compute dashboard stats for a role.
 */
export function statsForRole(role) {
  const items = listForRole(role);
  const total = items.length;
  const done = items.filter((i) => i.status === 'done').length;
  const open = total - done;
  return { total, done, open };
}

/**
 * Compute admin overview stats across the whole workspace.
 */
export function adminStats() {
  const items = getItems();
  const total = items.length;
  const done = items.filter((i) => i.status === 'done').length;
  const contributors = new Set(items.flatMap((i) => [i.createdBy, ...i.assignees]).filter(Boolean)).size;
  return { total, done, contributors };
}
